// modules/facturacion/components/FacturacionMasivaTab.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert,
  CircularProgress, Checkbox, Tooltip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Divider, TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RefreshIcon     from "@mui/icons-material/Refresh";
import ReceiptIcon     from "@mui/icons-material/Receipt";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon      from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import PrefacturaServicioModal from "./PrefacturaServicioModal";
import PrefacturaComparativaDialog from "./PrefacturaComparativaDialog";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
};

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];

const normalizarTexto = (value) =>
  String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const normalizarSucursal = (value) => {
  const sucursal = normalizarTexto(value);
  if (sucursal.includes("santa cruz")) return "Santa Cruz";
  if (sucursal.includes("cochabamba")) return "Cochabamba";
  if (sucursal.includes("la paz") || sucursal.includes("el alto")) return "La Paz";
  return "";
};

// state y setState vienen de FacturacionPage — persisten entre tabs
const FacturacionMasivaTab = ({ state, setState }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    lista,
    sucursal,
    seleccionados,
    buscar = "",
    page = 1,
    pageSize = 30,
    total = 0,
  } = state;
  const [buscarTexto, setBuscarTexto] = useState(buscar);

  const setLista = useCallback((v) =>
    setState((p) => ({ ...p, lista: typeof v === "function" ? v(p.lista) : v })), [setState]);
  const setSucursal = useCallback((v) =>
    setState((p) => ({ ...p, sucursal: v, page: 1 })), [setState]);
  const setSeleccionados = useCallback((v) =>
    setState((p) => ({ ...p, seleccionados: typeof v === "function" ? v(p.seleccionados) : v })), [setState]);
  const setMasivaMeta = useCallback((meta) =>
    setState((p) => ({ ...p, ...meta })), [setState]);

  const [loading,   setLoading]   = useState(false);
  const [generando, setGenerando] = useState(false);
  const [error,     setError]     = useState(null);
  const [modalData, setModalData] = useState(null);
  const [comparativaServicios, setComparativaServicios] = useState(null);

  const sucursalUsuario = user?.sucursal || "";
  const puedeGenerarLista = normalizarTexto(user?.role) === "cobranzas";
  const sucursalCoincide = normalizarTexto(sucursal) === normalizarTexto(sucursalUsuario);
  const generarListaBloqueado = generando || !puedeGenerarLista || !sucursalCoincide;
  const generarListaTooltip = !puedeGenerarLista
    ? "Solo usuarios con rol cobranzas pueden generar la lista."
    : !sucursalCoincide
      ? `Solo puedes generar la lista de tu sucursal (${sucursalUsuario || "sin sucursal"}).`
      : "";

  useEffect(() => {
    const sucursalPerfil = normalizarSucursal(user?.sucursal);
    if (!sucursalPerfil) return;
    setState((prev) => (
      prev.sucursal
        ? prev
        : { ...prev, sucursal: sucursalPerfil, page: 1, seleccionados: [] }
    ));
  }, [setState, user?.sucursal]);

  // Cargar lista desde temp_fact_masiva al montar y al cambiar sucursal
  const fetchLista = useCallback(async (suc, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        sucursal: suc,
        buscar: options.buscar ?? buscar,
        page: options.page ?? page,
        pageSize: options.pageSize ?? pageSize,
      };
      const res = await axios.get("/api/facturas/masiva", { params });
      const data = res.data?.data ?? res.data;
      if (Array.isArray(data)) {
        setLista(data);
        setMasivaMeta({ total: data.length, page: 1, pageSize: data.length || pageSize });
      } else {
        setLista(Array.isArray(data?.items) ? data.items : []);
        setMasivaMeta({
          total: Number(data?.total || 0),
          page: Number(data?.page || params.page),
          pageSize: Number(data?.limit || params.pageSize),
        });
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Error al cargar la lista");
    } finally {
      setLoading(false);
    }
  }, [buscar, page, pageSize, setLista, setMasivaMeta]);

  useEffect(() => {
    if (!sucursal) return;
    fetchLista(sucursal);
  }, [sucursal, buscar, page, pageSize, fetchLista]);

  // Generar/regenerar temp_fact_masiva
  const handleGenerar = async () => {
    if (!puedeGenerarLista) {
      setError("Solo usuarios con rol cobranzas pueden generar la lista.");
      return;
    }
    if (!sucursalCoincide) {
      setError(`Solo puedes generar la lista de tu sucursal (${sucursalUsuario || "sin sucursal"}).`);
      return;
    }
    if (!window.confirm(
      `¿Generar la lista de ${sucursal}? Esto borra la lista actual (${total} servicios) y la arma de nuevo desde cero. Si tenías servicios seleccionados o filtrados, se pierden.`
    )) {
      return;
    }

    setGenerando(true);
    setError(null);
    setSeleccionados([]);
    try {
      await axios.post("/api/facturas/masiva/generar", { sucursal });
      setMasivaMeta({ page: 1 });
      await fetchLista(sucursal, { page: 1 });
    } catch (e) {
      setError(e?.response?.data?.message || "Error al generar la lista");
    } finally {
      setGenerando(false);
    }
  };

  const toggleAll = (checked) => {
    if (checked) {
      setSeleccionados((prev) => Array.from(new Set([...prev, ...lista.map((s) => s.cod_servicio_cliente)])));
    } else {
      const idsPagina = new Set(lista.map((s) => s.cod_servicio_cliente));
      setSeleccionados((prev) => prev.filter((id) => !idsPagina.has(id)));
    }
  };

  const toggleOne = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAccionMasiva = (tipo) => {
    const serviciosSel = lista.filter((s) => seleccionados.includes(s.cod_servicio_cliente));
    if (!serviciosSel.length) return;
    setModalData({ servicios: serviciosSel, tipo, index: 0, masivo: true });
  };

  const handleAbrirComparativa = () => {
    const serviciosSel = lista.filter((s) => seleccionados.includes(s.cod_servicio_cliente));
    if (!serviciosSel.length) return;
    setComparativaServicios(serviciosSel);
  };

  const handleComparativaSuccess = () => {
    fetchLista(sucursal);
  };

  const handleModalSuccess = (codFactura) => {
    const { servicios, index, masivo } = modalData;
    const codServicio = servicios[index].cod_servicio_cliente;

    setLista((prev) => prev.filter((s) => s.cod_servicio_cliente !== codServicio));
    setSeleccionados((prev) => prev.filter((x) => x !== codServicio));
    setMasivaMeta({ total: Math.max(0, total - 1) });

    if (masivo) {
      const siguiente = index + 1;
      if (siguiente < servicios.length) {
        setModalData((prev) => ({ ...prev, index: siguiente }));
      } else {
        setModalData(null);
      }
      return;
    }

    setModalData(null);
    if (codFactura) {
      navigate(`/facturacion/facturas/${codFactura}`, {
        state: {
          from: "/facturacion",
          cod_servicio_cliente: codServicio,
        },
      });
    } else {
      navigate(`/reporte-cliente/${codServicio}`);
    }
  };

  const allChecked  = lista.length > 0 && lista.every((s) => seleccionados.includes(s.cod_servicio_cliente));
  const someChecked = lista.some((s) => seleccionados.includes(s.cod_servicio_cliente)) && !allChecked;

  const handleBuscar = () => {
    setMasivaMeta({ buscar: buscarTexto.trim(), page: 1 });
  };

  return (
    <Box>
      {/* ── Filtro de sucursal ── */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
        <TextField
          select size="small" label="Sucursal"
          sx={{ ...compactFieldSx, width: 135 }}
          value={sucursal}
          onChange={(e) => setSucursal(e.target.value)}
        >
          {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>)}
        </TextField>

        <TextField
          size="small"
          label="Buscar razon social"
          value={buscarTexto}
          onChange={(e) => setBuscarTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleBuscar();
          }}
          sx={{ ...compactFieldSx, width: { xs: "100%", sm: 260 } }}
        />

        <Button
          size="small"
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleBuscar}
          sx={{ minHeight: 32 }}
        >
          Buscar
        </Button>

        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          Total: {total}
        </Typography>

        {loading && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Tabla ── */}
      {!loading && lista.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No hay servicios en la lista para {sucursal}. Usa el botón "Generar Lista" para crearla.
        </Alert>
      ) : (
        <>
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.25, px: 0.75, fontSize: "0.78rem" } }}>
                <TableHead>
                  <TableRow>
                    <StyledHeaderCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </StyledHeaderCell>
                    <StyledHeaderCell>COD Servicio</StyledHeaderCell>
                    <StyledHeaderCell>COD Cliente</StyledHeaderCell>
                    <StyledHeaderCell>Razón Social</StyledHeaderCell>
                    <StyledHeaderCell>Grupo</StyledHeaderCell>
                    <StyledHeaderCell>Nombre servicio</StyledHeaderCell>
                    <StyledHeaderCell>Servicio</StyledHeaderCell>
                    <StyledHeaderCell>Tipo</StyledHeaderCell>
                    <StyledHeaderCell>Sucursal</StyledHeaderCell>
                    <StyledHeaderCell>Descripción</StyledHeaderCell>
                    <StyledHeaderCell align="center">Acción</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    lista.map((s) => (
                      <TableRow
                        key={s.cod_servicio_cliente}
                        hover
                        selected={seleccionados.includes(s.cod_servicio_cliente)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={seleccionados.includes(s.cod_servicio_cliente)}
                            onChange={() => toggleOne(s.cod_servicio_cliente)}
                          />
                        </TableCell>
                        <TableCell>{s.cod_servicio_cliente}</TableCell>
                        <TableCell>{s.cod_cliente}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.78rem", lineHeight: 1.25 }}>
                            {s.razon_social}
                          </Typography>
                        </TableCell>
                        <TableCell>{s.grupo || "-"}</TableCell>
                        <TableCell>{s.cliente_servicio_nombre || "-"}</TableCell>
                        <TableCell>{s.servicio_nombre}</TableCell>
                        <TableCell>{s.tipo}</TableCell>
                        <TableCell>{s.sucursal_facturar}</TableCell>
                        <TableCell>{s.descripcion}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                            <Tooltip title="Pre-facturar">
                              <IconButton size="small" color="warning"
                                onClick={() => setModalData({ servicios: [s], tipo: "pre", index: 0, masivo: false })}>
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Facturar">
                              <IconButton size="small" color="primary"
                                onClick={() => setModalData({ servicios: [s], tipo: "fac", index: 0, masivo: false })}>
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <TablePagination
            component="div"
            count={total}
            page={Math.max(page - 1, 0)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[30, 50, 100, 200, 500]}
            labelRowsPerPage="Limite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => setMasivaMeta({ page: newPage + 1 })}
            onRowsPerPageChange={(e) => {
              setMasivaMeta({ pageSize: Number(e.target.value), page: 1 });
            }}
            sx={{
              minHeight: 36,
              "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 1 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.78rem",
              },
            }}
          />

          {/* Botones masivos */}
          {seleccionados.length > 0 && (
            <Box sx={{ mt: 1, display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                {seleccionados.length} seleccionados
              </Typography>
              <Button size="small" variant="outlined" color="warning"
                startIcon={<DescriptionIcon />}
                onClick={() => handleAccionMasiva("pre")}>
                Pre-facturar seleccionados
              </Button>
              <Button size="small" variant="outlined" color="secondary"
                startIcon={<CompareArrowsIcon />}
                onClick={handleAbrirComparativa}>
                Pre-facturar (comparativo)
              </Button>
              <Button size="small" variant="contained" color="primary"
                startIcon={<ReceiptIcon />}
                onClick={() => handleAccionMasiva("fac")}>
                Facturar seleccionados
              </Button>
            </Box>
          )}
        </>
      )}

      {/* ── Botón Generar Lista al final ── */}
      <Divider sx={{ mt: 1.5, mb: 1 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <Tooltip title={generarListaTooltip}>
          <span>
            <Button
              size="small"
              variant="contained"
              color="secondary"
              startIcon={generando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={handleGenerar}
              disabled={generarListaBloqueado}
            >
              {generando ? "Generando..." : "Generar Lista"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Modal prefactura/factura */}
      {modalData && (
        <PrefacturaServicioModal
          open={!!modalData}
          onClose={() => setModalData(null)}
          onSuccess={handleModalSuccess}
          servicio={modalData.servicios[modalData.index]}
          tipo={modalData.tipo}
          progreso={modalData.servicios.length > 1
            ? { actual: modalData.index + 1, total: modalData.servicios.length }
            : null}
        />
      )}

      {/* Vista comparativa masiva */}
      {comparativaServicios && (
        <PrefacturaComparativaDialog
          open={!!comparativaServicios}
          servicios={comparativaServicios}
          onClose={() => setComparativaServicios(null)}
          onSuccess={handleComparativaSuccess}
        />
      )}
    </Box>
  );
};

export default FacturacionMasivaTab;

